import { useEffect } from 'react'

import { useToast } from '@chakra-ui/react'
import { useChainModal, useConnectModal } from '@rainbow-me/rainbowkit'
import { useQueryClient } from '@tanstack/react-query'
import { formatUnits } from 'viem'
import { avalanche, avalancheFuji } from 'viem/chains'
import { useAccount } from 'wagmi'

import { Button } from '@/common/components/Button'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import useDepositAVAX from '@/hooks/depositAVAX'
import useRedeemAVAX from '@/hooks/redeemAVAX'
import { usePreviewWithdraw } from '@/hooks/useAvaxBalanceOfggAvax'
import { useVaultRatio } from '@/hooks/useGetVaultData'
import { displayBN } from '@/utils/numberFormatter'

export interface ApproveProps {
  amount: bigint
  balance: bigint
  maxDeposit?: bigint
  amountGreaterThanPool?: boolean
  amountGreaterThanCap?: boolean
  swapDirection?: boolean
  clearAmount?: () => void
}

const ApproveAVAXButton = ({
  amount,
  balance,
  maxDeposit,
  amountGreaterThanPool,
  amountGreaterThanCap,
  swapDirection,
  clearAmount,
}: ApproveProps) => {
  const { isConnected, chain, address } = useAccount()
  const { openConnectModal } = useConnectModal()
  const { openChainModal } = useChainModal()
  const { data: ratio } = useVaultRatio()
  const ratioDecimal = ratio && parseFloat(formatUnits(ratio, 18))
  const { currentToken } = useValueTokens()

  const toast = useToast()
  const queryClient = useQueryClient()

  const {
    isPending: depositIsPending,
    writeError: depositError,
    isLoading: depositIsLoading,
    isSuccess: depositIsSuccess,
    receipt: depositReceipt,
    isError: depositIsError,
    deposit,
  } = useDepositAVAX(amount)
  const { data: previewWithdrawData } = usePreviewWithdraw(amount, swapDirection)
  const {
    isPending: redeemIsPending,
    writeError: redeemError,
    isLoading: redeemIsLoading,
    isSuccess: redeemIsSuccess,
    receipt: redeemReceipt,
    isError: redeemIsError,
    redeem,
  } = useRedeemAVAX(previewWithdrawData)

  const showError = () => {
    toast({
      position: 'top',
      title: `Error during ${!swapDirection ? 'deposit' : 'redeem'} of AVAX`,
      status: 'error',
      duration: 20000,
      isClosable: true,
    })
  }

  useEffect(() => {
    if (depositIsSuccess && depositReceipt) {
      if (depositReceipt.status === 'success') {
        toast({
          position: 'top',
          title: 'Deposit successful',
          status: 'info',
          duration: 8000,
        })
        queryClient.invalidateQueries() // Refetch everything

        clearAmount && clearAmount()
        depositAssets(address, amount.toString(), ratioDecimal.toString())
      } else {
        showError()
      }
    } else if (depositIsError) {
      showError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositIsSuccess, depositReceipt, depositIsError, toast])

  useEffect(() => {
    if (redeemIsSuccess && redeemReceipt) {
      if (redeemReceipt.status === 'success') {
        toast({
          position: 'top',
          title: 'Redeem successful',
          status: 'info',
          duration: 8000,
        })

        queryClient.invalidateQueries() // Refetch everything

        clearAmount && clearAmount()
        redeemAssets(address, amount.toString(), ratioDecimal.toString())
      } else {
        showError()
      }
    } else if (redeemIsError) {
      showError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemIsSuccess, redeemReceipt, redeemIsError, toast])

  if (chain?.id && chain.id != avalanche.id && chain.id != avalancheFuji.id) {
    return <Button onClick={openChainModal}>Wrong network</Button>
  }

  if (amountGreaterThanCap && !swapDirection) {
    return (
      <div className="flex w-full gap-4">
        <Button disabled full variant="destructive-outline">
          Max deposit amount is {maxDeposit ? displayBN(maxDeposit) : '0.00'} based on vault cap
        </Button>
      </div>
    )
  }

  if (amountGreaterThanPool && swapDirection) {
    return (
      <div className="flex w-full gap-4">
        <Button disabled full variant="destructive-outline">
          Insufficient Liquidity in Pool
        </Button>
      </div>
    )
  }

  if (amount > balance) {
    return (
      <div className="flex w-full gap-4">
        <Button disabled full variant="destructive-outline">
          Insufficient balance
        </Button>
      </div>
    )
  }

  return isConnected ? (
    <Button
      backgroundColor={currentToken.buttonBackgroundColor}
      disabled={
        !amount ||
        amount <= 0 ||
        depositIsLoading ||
        depositIsPending ||
        redeemIsLoading ||
        redeemIsPending
      }
      height="14"
      isLoading={depositIsLoading || depositIsPending || redeemIsLoading || redeemIsPending}
      onClick={() => (!swapDirection ? deposit() : redeem())}
      size="xl"
      textColor={currentToken.buttonTextColor}
      variant="avax"
    >
      {!swapDirection ? 'Deposit' : 'Redeem'} AVAX
    </Button>
  ) : (
    <Button data-testid="connect" onClick={openConnectModal} size="sm">
      Connect Wallet
    </Button>
  )
}

export async function redeemAssets(
  userAddress: string,
  redeemAmount: string,
  currentPrice: string,
): Promise<any | null> {
  try {
    const res = await fetch('/api/redeem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userAddress, redeemAmount, currentPrice }),
    })

    if (!res.ok) {
      throw new Error(`Failed to redeem assets: ${res.statusText}`)
    }

    const data = await res.json()
    return data
  } catch (err) {
    console.error('Error redeeming assets:', err)
    return null
  }
}

async function depositAssets(
  userAddress: string,
  depositAmount: string,
  currentPrice: string,
): Promise<any | null> {
  try {
    const res = await fetch('/api/deposit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userAddress, depositAmount, currentPrice }),
    })

    if (!res.ok) {
      throw new Error(`Failed to deposit assets: ${res.statusText}`)
    }

    const data: any = await res.json()
    return data
  } catch (err) {
    console.error('Error depositing assets:', err)
    return null
  }
}
export default ApproveAVAXButton
